import React from "react";
import { type MessageDescriptor, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { fetchWithAuth } from "~/common/fetch";
import { API_PATIENT_AUDIO, API_PATIENT_AUDIO_PREVIEW } from "~/config";
import {
  smartPrescriptionFailed,
  smartPrescriptionMicBlocked,
  smartPrescriptionRecordingStarted,
  smartPrescriptionRecordingStopped,
  smartPrescriptionRecipeUpdateStarted,
  smartPrescriptionUploadFailed,
  smartPrescriptionUploadFinished,
  smartPrescriptionUploadStarted,
} from "~/slices/smart_prescription";

import { removePendingAudioUpload, setPendingAudioUpload } from "../patient_new/audio_pending_uploads";
import { startWavRecorder, type WavRecorder } from "./wav-recorder";

type AudioFileLink = {
  file_id?: number;
  file_name: string;
  url: string;
};

export type AudioNoteErrorMessageId =
  | "additional.services.smart.audio.duration_limit"
  | "additional.services.smart.audio.upload_error"
  | "additional.services.smart.audio.unsupported_format"
  | "additional.services.smart.audio.recording_not_supported"
  | "additional.services.smart.audio.recording_error";

const SUPPORTED_AUDIO_EXTENSIONS = new Set(["ogg", "oga", "opus", "mp3", "wav", "pcm"]);
const MAX_TOTAL_AUDIO_DURATION_SECONDS = 30 * 60;
// Waveform pacing is independent of the recording limit.
export const RECORDING_WAVEFORM_SCROLL_WINDOW_SECONDS = 120;
export const RECORDING_WAVEFORM_BAR_COUNT = 150;
export const RECORDING_MAX_DURATION_SECONDS = 15 * 60;
const RECORDING_SAMPLE_RATE = 16000;
export const RECORDING_COUNTDOWN_WARNING_SECONDS = 30;
const AUDIO_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;
// 60 × 2s = 2min poll budget — long combined takes can need more than a short budget.
const TRANSCRIPT_POLL_ATTEMPTS = 60;
const TRANSCRIPT_POLL_INTERVAL_MS = 2000;

async function pollForReadyTranscript(
  fetchAttempt: (attemptIndex: number) => Promise<Response>,
  startingState: PatientAudioNoteValue,
  isStale: () => boolean,
  onUpdate: (nextState: PatientAudioNoteValue) => void,
  logContext: Record<string, unknown>,
): Promise<{ finalState: PatientAudioNoteValue; reachedReady: boolean }> {
  let nextState = startingState;
  for (const [attemptIndex] of Array.from({ length: TRANSCRIPT_POLL_ATTEMPTS }).entries()) {
    if (isStale()) {
      return { finalState: nextState, reachedReady: false };
    }
    const response = await fetchAttempt(attemptIndex);
    if (isStale()) {
      return { finalState: nextState, reachedReady: false };
    }

    if (!response.ok) {
      console.warn("[audio-note] polling request failed", {
        ...logContext,
        attempt: attemptIndex + 1,
        status: response.status,
      });
    } else {
      const payload = (await response.json()) as { audio_note?: PatientAudioNoteValue | null };
      if (payload.audio_note && !isStale()) {
        nextState = { ...nextState, ...payload.audio_note };
        onUpdate(nextState);
        console.info("[audio-note] polling result", {
          ...logContext,
          attempt: attemptIndex + 1,
          status: nextState.transcript_status,
          hasTranscript: Boolean(nextState.transcript_text),
        });
        if (nextState.transcript_status === "ready" || nextState.transcript_status === "empty") {
          return { finalState: nextState, reachedReady: true };
        }
      }
    }

    await new Promise((resolve) => window.setTimeout(resolve, TRANSCRIPT_POLL_INTERVAL_MS));
  }
  return { finalState: nextState, reachedReady: false };
}

function micBlockedMessageId(error: unknown): MessageDescriptor["id"] {
  const name = error instanceof DOMException ? error.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "additional.services.smart.audio.mic_blocked";
    case "NotFoundError":
    case "OverconstrainedError":
      return "additional.services.smart.audio.mic_not_found";
    case "NotReadableError":
      return "additional.services.smart.audio.mic_busy";
    default:
      return "additional.services.smart.audio.mic_unavailable";
  }
}

function getFileExtension(fileName: string): string | null {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === fileName.length - 1) {
    return null;
  }
  return fileName.slice(dotIndex + 1).toLowerCase();
}

export type PatientAudioNoteValue = {
  storage_resource_id?: number | null;
  uuid?: string | null;
  file_name?: string | null;
  duration_seconds?: number | null;
  transcript_text?: string | null;
  transcript_warning?: string | null;
  transcript_status?: "empty" | "processing" | "ready";
  is_locked?: boolean;
  uploads?: Array<{
    storage_resource_id?: number | null;
    client_id?: string;
    uuid?: string | null;
    file_name?: string | null;
    duration_seconds?: number | null;
    uploaded_at?: number;
  }>;
  files?: AudioFileLink[];
};

export type AudioNoteEntry = {
  id: string;
  fileName: string;
  durationSeconds: number | null;
  uploadedAt: number;
  sourceUrl: string | null;
  isRemovable: boolean;
};

export type AudioNoteControllerProps = {
  audioNote: PatientAudioNoteValue | null | undefined;
  patientId?: number;
  clinicId?: number;
  disabled?: boolean;
  forceEditable?: boolean;
  deferUploadUntilSubmit?: boolean;
  recordTriggerRef?: React.MutableRefObject<(() => void) | null>;
  waveformBarCount?: number;
  onChange(nextValue: PatientAudioNoteValue): void;
};

export function useAudioNoteController({
  audioNote,
  patientId,
  clinicId,
  disabled = false,
  forceEditable = false,
  deferUploadUntilSubmit = false,
  recordTriggerRef,
  waveformBarCount = RECORDING_WAVEFORM_BAR_COUNT,
  onChange,
}: AudioNoteControllerProps) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = React.useState(false);
  const [isRecording, setIsRecording] = React.useState(false);
  const [recordingDurationSeconds, setRecordingDurationSeconds] = React.useState(0);
  const [playingAudioId, setPlayingAudioId] = React.useState<string | null>(null);
  const [playbackRates, setPlaybackRates] = React.useState<Record<string, number>>({});
  const [uploadProgressPercent, setUploadProgressPercent] = React.useState(0);
  const [errorMessage, setErrorMessage] = React.useState<AudioNoteErrorMessageId | null>(null);
  const [localAudioPreviews, setLocalAudioPreviews] = React.useState<Record<string, string>>({});
  const [pendingLocalEntries, setPendingLocalEntries] = React.useState<AudioNoteEntry[]>([]);
  const pendingLocalEntriesRef = React.useRef(pendingLocalEntries);
  pendingLocalEntriesRef.current = pendingLocalEntries;

  const sessionUuidRef = React.useRef<string | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const audioRefs = React.useRef<Record<string, HTMLAudioElement | null>>({});
  const recorderRef = React.useRef<WavRecorder | null>(null);
  const recorderStopHandlerRef = React.useRef<((blob: Blob | null) => Promise<void>) | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const recordingStartedAtRef = React.useRef<number>(0);
  const analyserRef = React.useRef<AnalyserNode | null>(null);
  const analysisAudioCtxRef = React.useRef<AudioContext | null>(null);
  const levelIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const waveformBarCountRef = React.useRef(waveformBarCount);
  waveformBarCountRef.current = waveformBarCount;
  const durationCapTimeoutRef = React.useRef<number | null>(null);
  const persistGenerationRef = React.useRef(0);
  const resumedProcessingRef = React.useRef(false);
  const pendingSegmentFilesRef = React.useRef<Array<{ file: File; durationSeconds: number }>>([]);

  const [levelHistory, setLevelHistory] = React.useState<number[]>([]);

  const stopLevelSampling = React.useCallback(() => {
    if (levelIntervalRef.current) {
      clearInterval(levelIntervalRef.current);
      levelIntervalRef.current = null;
    }
    if (analysisAudioCtxRef.current) {
      void analysisAudioCtxRef.current.close();
      analysisAudioCtxRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  const startLevelSampling = React.useCallback((stream: MediaStream) => {
    try {
      const analysisCtx = new window.AudioContext();
      const source = analysisCtx.createMediaStreamSource(stream);
      const analyser = analysisCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      analysisAudioCtxRef.current = analysisCtx;

      const barCount = waveformBarCountRef.current;
      const sampleIntervalMs = (RECORDING_WAVEFORM_SCROLL_WINDOW_SECONDS * 1000) / barCount;
      const timeDomainData = new Uint8Array(analyser.fftSize);

      levelIntervalRef.current = setInterval(() => {
        analyser.getByteTimeDomainData(timeDomainData);
        let sumSquares = 0;
        for (let i = 0; i < timeDomainData.length; i++) {
          const normalized = ((timeDomainData[i] ?? 128) - 128) / 128;
          sumSquares += normalized * normalized;
        }
        const level = Math.min(1, Math.sqrt(sumSquares / timeDomainData.length) * 6);
        setLevelHistory((prev) => {
          const next = [...prev, level];
          return next.length > barCount ? next.slice(next.length - barCount) : next;
        });
      }, sampleIntervalMs);
    } catch (analyserError) {
      console.error(analyserError);
    }
  }, []);

  const isLocked = !forceEditable && (disabled || audioNote?.is_locked === true);
  const canRemoveEntries = !disabled && audioNote?.is_locked !== true && !patientId;
  const uploads = audioNote?.uploads ?? [];
  const totalAudioDurationSeconds = uploads.reduce((acc, upload) => {
    const duration = upload.duration_seconds;
    return acc + (typeof duration == "number" ? duration : 0);
  }, 0);
  const hasDurationLimitReached = totalAudioDurationSeconds >= MAX_TOTAL_AUDIO_DURATION_SECONDS;
  const removeDisabledTooltip = intl.formatMessage({
    id: "additional.services.smart.audio.remove_disabled_tooltip",
  });
  const formatDuration = React.useCallback((durationInSeconds: number) => {
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = durationInSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }, []);
  const totalDurationFormatted = formatDuration(totalAudioDurationSeconds);
  const maxDurationFormatted = formatDuration(MAX_TOTAL_AUDIO_DURATION_SECONDS);
  const liveTotalDurationFormatted = formatDuration(totalAudioDurationSeconds + (isRecording ? recordingDurationSeconds : 0));
  // XHR, not fetchWithAuth: the Fetch API has no upload-progress events for request bodies.
  const uploadAudioWithProgress = React.useCallback(
    (uuid: string, file: File) =>
      new Promise<void>((resolve, reject) => {
        const form = new FormData();
        form.append("uuid", uuid);
        form.append("file", file);
        if (clinicId != null) {
          form.append("clinic_id", String(clinicId));
        }

        const request = new XMLHttpRequest();
        request.open("POST", `${process.env.API}/patients/media/audio`);
        request.withCredentials = true;
        request.timeout = AUDIO_UPLOAD_TIMEOUT_MS;
        request.upload.onprogress = (event) => {
          if (!event.lengthComputable) {
            return;
          }
          setUploadProgressPercent(Math.round((event.loaded / event.total) * 100));
        };
        request.onload = () => {
          if (request.status >= 200 && request.status < 300) {
            resolve();
            return;
          }
          reject(new Error(`Audio upload failed with status ${request.status}`));
        };
        request.onerror = () => reject(new Error("Audio upload network error"));
        request.ontimeout = () => reject(new Error("Audio upload timed out"));
        request.send(form);
      }),
    [clinicId],
  );

  React.useEffect(() => {
    if (!isRecording) {
      setRecordingDurationSeconds(0);
      return;
    }

    const intervalId = window.setInterval(() => {
      setRecordingDurationSeconds(Math.max(0, Math.round((Date.now() - recordingStartedAtRef.current) / 1000)));
    }, 300);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isRecording]);

  React.useEffect(() => {
    return () => {
      for (const previewUrl of Object.values(localAudioPreviews)) {
        if (previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl);
        }
      }
    };
  }, [localAudioPreviews]);

  React.useEffect(() => {
    return () => {
      if (durationCapTimeoutRef.current) {
        window.clearTimeout(durationCapTimeoutRef.current);
        durationCapTimeoutRef.current = null;
      }
      dispatch(smartPrescriptionRecordingStopped());

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      stopLevelSampling();

      for (const entry of pendingLocalEntriesRef.current) {
        if (entry.sourceUrl) {
          URL.revokeObjectURL(entry.sourceUrl);
        }
      }

      for (const audio of Object.values(audioRefs.current)) {
        audio?.pause();
      }
    };
  }, [dispatch, stopLevelSampling]);

  const persistAudioNote = React.useCallback(
    async (nextAudioNote: PatientAudioNoteValue) => {
      const myGeneration = ++persistGenerationRef.current;
      const uploadsCount = nextAudioNote.uploads?.length ?? 0;
      if (uploadsCount === 0) {
        onChange({ ...nextAudioNote, transcript_status: "empty" });
        return;
      }

      const isStale = () => persistGenerationRef.current !== myGeneration;

      try {
        if (!patientId) {
          const startingState: PatientAudioNoteValue = {
            ...nextAudioNote,
            transcript_status: "processing",
          };
          if (isStale()) {
            return;
          }
          onChange(startingState);

          const { finalState, reachedReady } = await pollForReadyTranscript(
            () =>
              fetchWithAuth(API_PATIENT_AUDIO_PREVIEW, {
                method: "POST",
                body: JSON.stringify({ audio_note: nextAudioNote }),
              }),
            startingState,
            isStale,
            onChange,
            { uploadsCount },
          );
          if (!reachedReady && !isStale()) {
            onChange({ ...finalState, transcript_status: "empty" });
            dispatch(smartPrescriptionFailed(intl.formatMessage({ id: "additional.services.smart.audio.processing_error" })));
          }
          return;
        }

        const response = await fetchWithAuth(API_PATIENT_AUDIO(patientId), {
          method: "PUT",
          body: JSON.stringify({ audio_note: nextAudioNote }),
        });
        if (!response.ok) {
          throw new Error("Failed to sync audio note");
        }
        if (isStale()) {
          return;
        }

        const payload = (await response.json()) as { audio_note?: PatientAudioNoteValue | null };
        const startingState = payload.audio_note ?? nextAudioNote;

        const shouldPollTranscript = (startingState.uploads?.length ?? 0) > 0 && (startingState.transcript_status === "processing" || !startingState.transcript_text);
        if (!shouldPollTranscript) {
          if (!isStale()) {
            onChange(startingState);
          }
          return;
        }

        const { finalState, reachedReady } = await pollForReadyTranscript(() => fetchWithAuth(API_PATIENT_AUDIO(patientId)), startingState, isStale, onChange, { patientId });
        if (!isStale()) {
          if (!reachedReady) {
            onChange({ ...finalState, transcript_status: "empty" });
            dispatch(smartPrescriptionFailed(intl.formatMessage({ id: "additional.services.smart.audio.processing_error" })));
          } else {
            onChange(finalState);
          }
        }
      } catch (error) {
        console.error(error);
        if (!isStale()) {
          onChange({ ...nextAudioNote, transcript_status: "empty" });
          dispatch(smartPrescriptionFailed(intl.formatMessage({ id: "additional.services.smart.audio.processing_error" })));
        }
      }
    },
    [dispatch, intl, onChange, patientId],
  );

  React.useEffect(() => {
    if (patientId != null || resumedProcessingRef.current || persistGenerationRef.current !== 0 || audioNote?.transcript_status !== "processing") {
      return;
    }

    resumedProcessingRef.current = true;
    void persistAudioNote(audioNote);
  }, [audioNote, patientId, persistAudioNote]);

  const getAudioDurationInSeconds = React.useCallback(async (file: File): Promise<number | null> => {
    const objectUrl = URL.createObjectURL(file);
    const audio = new Audio(objectUrl);

    return new Promise((resolve) => {
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(Number.isFinite(audio.duration) ? Math.round(audio.duration) : null);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
    });
  }, []);

  const getSessionUuid = React.useCallback(() => {
    if (!sessionUuidRef.current) {
      sessionUuidRef.current = uuidv4();
    }
    return sessionUuidRef.current;
  }, []);

  const appendUpload = React.useCallback(
    (upload: { client_id: string; uuid?: string; file_name: string; duration_seconds: number | null; uploaded_at: number }) => {
      const nextUploads = [...(audioNote?.uploads ?? [])];
      const existed = nextUploads.some((entry) => entry.client_id === upload.client_id);
      if (!existed) {
        nextUploads.push(upload);
      }
      return nextUploads;
    },
    [audioNote?.uploads],
  );

  const uploadAudioFile = React.useCallback(
    async (file: File, forcedDuration?: number | null) => {
      setIsUploading(true);
      setUploadProgressPercent(0);
      setErrorMessage(null);

      try {
        const extension = getFileExtension(file.name);
        if (!extension || !SUPPORTED_AUDIO_EXTENSIONS.has(extension)) {
          console.warn("[audio-note] unsupported upload format", { fileName: file.name });
          setErrorMessage("additional.services.smart.audio.unsupported_format");
          dispatch(smartPrescriptionUploadFinished());
          return;
        }

        const durationSeconds = typeof forcedDuration == "number" ? forcedDuration : await getAudioDurationInSeconds(file);
        const durationForLimit = typeof durationSeconds == "number" ? durationSeconds : 0;
        if (totalAudioDurationSeconds + durationForLimit > MAX_TOTAL_AUDIO_DURATION_SECONDS) {
          setErrorMessage("additional.services.smart.audio.duration_limit");
          dispatch(smartPrescriptionUploadFinished());
          return;
        }

        if (deferUploadUntilSubmit) {
          const uploadId = `local:${uuidv4()}`;
          const previewUrl = URL.createObjectURL(file);
          setPendingAudioUpload(uploadId, file);
          setLocalAudioPreviews((prev) => ({ ...prev, [uploadId]: previewUrl }));

          const nextAudioNote: PatientAudioNoteValue = {
            ...(audioNote ?? {}),
            uploads: appendUpload({
              client_id: uploadId,
              file_name: file.name,
              duration_seconds: durationSeconds,
              uploaded_at: Date.now(),
            }),
            transcript_text: "",
            transcript_status: "empty",
          };
          onChange(nextAudioNote);
          return;
        }

        dispatch(smartPrescriptionUploadStarted());
        const uuid = getSessionUuid();
        await uploadAudioWithProgress(uuid, file);

        const uploadId = `${uuid}:${file.name}`;
        const previewUrl = URL.createObjectURL(file);
        setLocalAudioPreviews((prev) => ({ ...prev, [uploadId]: previewUrl }));

        const nextAudioNote: PatientAudioNoteValue = {
          ...(audioNote ?? {}),
          uploads: appendUpload({
            client_id: uploadId,
            uuid,
            file_name: file.name,
            duration_seconds: durationSeconds,
            uploaded_at: Date.now(),
          }),
          transcript_text: "",
          transcript_status: "processing",
        };
        onChange(nextAudioNote);
        dispatch(smartPrescriptionUploadFinished());
        void persistAudioNote(nextAudioNote);
      } catch (error) {
        console.error(error);
        const message = intl.formatMessage({
          id: "additional.services.smart.audio.upload_error",
        });
        setErrorMessage("additional.services.smart.audio.upload_error");
        dispatch(smartPrescriptionUploadFailed(message));
      } finally {
        setIsUploading(false);
        setUploadProgressPercent(0);
      }
    },
    [appendUpload, audioNote, deferUploadUntilSubmit, dispatch, getAudioDurationInSeconds, getSessionUuid, intl, onChange, persistAudioNote, totalAudioDurationSeconds, uploadAudioWithProgress],
  );
  // Ref so the batch-upload loop below always calls the latest closure — a closure
  // captured once at the loop's start would append every segment onto the same
  // stale `audioNote.uploads`, clobbering instead of accumulating.
  const uploadAudioFileRef = React.useRef(uploadAudioFile);
  uploadAudioFileRef.current = uploadAudioFile;

  const handleFileUpload = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      event.target.value = "";

      if (files.length === 0 || isLocked) {
        return;
      }

      for (const file of files) {
        await uploadAudioFile(file);
      }
    },
    [isLocked, uploadAudioFile],
  );

  const stopRecording = React.useCallback(() => {
    if (durationCapTimeoutRef.current) {
      window.clearTimeout(durationCapTimeoutRef.current);
      durationCapTimeoutRef.current = null;
    }
    const recorder = recorderRef.current;
    const onStopped = recorderStopHandlerRef.current;
    if (!recorder) {
      return;
    }
    setIsRecording(false);
    stopLevelSampling();
    dispatch(smartPrescriptionRecordingStopped());
    if (!deferUploadUntilSubmit) {
      dispatch(smartPrescriptionUploadStarted());
    }
    recorderStopHandlerRef.current = null;
    void recorder.stop().then(
      (blob) => onStopped?.(blob),
      () => onStopped?.(null),
    );
  }, [deferUploadUntilSubmit, dispatch, stopLevelSampling]);

  const beginRecorderSegment = React.useCallback(
    async (stream: MediaStream) => {
      const recorder = await startWavRecorder(stream, RECORDING_SAMPLE_RATE);

      recordingStartedAtRef.current = Date.now();
      setRecordingDurationSeconds(0);
      stopLevelSampling();
      setLevelHistory([]);
      startLevelSampling(stream);

      const handleStopped = async (blob: Blob | null) => {
        const durationSeconds = Math.round((Date.now() - recordingStartedAtRef.current) / 1000);

        if (blob && blob.size > 0) {
          const file = new File([blob], `audio-note-${Date.now()}.wav`, { type: "audio/wav" });
          pendingSegmentFilesRef.current.push({ file, durationSeconds });
          const localId = `local-segment:${Date.now()}`;
          const localSourceUrl = URL.createObjectURL(file);
          setPendingLocalEntries((prev) => [
            ...prev,
            {
              id: localId,
              fileName: file.name,
              durationSeconds,
              uploadedAt: Date.now(),
              sourceUrl: localSourceUrl,
              isRemovable: false,
            },
          ]);
        } else {
          setErrorMessage("additional.services.smart.audio.recording_error");
          if (!deferUploadUntilSubmit) {
            dispatch(smartPrescriptionUploadFinished());
          }
        }

        if (recorderRef.current === recorder) {
          recorderRef.current = null;
        }

        const pendingFiles = pendingSegmentFilesRef.current;
        pendingSegmentFilesRef.current = [];
        for (const pending of pendingFiles) {
          await uploadAudioFileRef.current(pending.file, pending.durationSeconds);
        }
        setPendingLocalEntries((prev) => {
          for (const entry of prev) {
            if (entry.sourceUrl) {
              URL.revokeObjectURL(entry.sourceUrl);
            }
          }
          return [];
        });

        stopLevelSampling();
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };

      recorderStopHandlerRef.current = handleStopped;
      recorderRef.current = recorder;

      if (durationCapTimeoutRef.current) {
        window.clearTimeout(durationCapTimeoutRef.current);
      }
      durationCapTimeoutRef.current = window.setTimeout(() => {
        if (recorderRef.current === recorder) {
          stopRecording();
        }
      }, RECORDING_MAX_DURATION_SECONDS * 1000);
    },
    [deferUploadUntilSubmit, dispatch, startLevelSampling, stopLevelSampling, stopRecording],
  );

  const startRecording = React.useCallback(async () => {
    if (isLocked) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder == "undefined") {
      setErrorMessage("additional.services.smart.audio.recording_not_supported");
      return;
    }

    if (hasDurationLimitReached) {
      setErrorMessage("additional.services.smart.audio.duration_limit");
      return;
    }

    setErrorMessage(null);
    pendingSegmentFilesRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      dispatch(smartPrescriptionRecordingStarted());
      await beginRecorderSegment(stream);
      setIsRecording(true);
    } catch (error) {
      console.error(error);
      dispatch(smartPrescriptionMicBlocked(intl.formatMessage({ id: micBlockedMessageId(error) })));
      setIsRecording(false);
      stopLevelSampling();
    }
  }, [hasDurationLimitReached, isLocked, beginRecorderSegment, dispatch, intl, stopLevelSampling]);

  React.useEffect(() => {
    if (!recordTriggerRef) {
      return;
    }
    recordTriggerRef.current = () => {
      void startRecording();
    };
    return () => {
      recordTriggerRef.current = null;
    };
  }, [recordTriggerRef, startRecording]);

  const existingAudioFiles = audioNote?.files ?? [];
  const existingUrlsByFileName = React.useMemo(() => Object.fromEntries(existingAudioFiles.map((file) => [file.file_name, file.url])), [existingAudioFiles]);
  const existingUrlsByFileId = React.useMemo(() => Object.fromEntries(existingAudioFiles.filter((file) => file.file_id != null).map((file) => [file.file_id, file.url])), [existingAudioFiles]);
  const allAudioEntries: AudioNoteEntry[] = React.useMemo(() => {
    const uploadedEntries = uploads.map((upload, index) => {
      const fallbackId = `${upload.uuid ?? "uploaded"}:${upload.file_name ?? "audio"}:${index}`;
      const entryId = upload.client_id ?? fallbackId;
      const fileName = upload.file_name ?? `audio-${index + 1}`;
      return {
        id: entryId,
        fileName,
        durationSeconds: upload.duration_seconds ?? null,
        uploadedAt: upload.uploaded_at ?? Date.now(),
        sourceUrl:
          localAudioPreviews[entryId] ||
          (upload.storage_resource_id != null ? existingUrlsByFileId[upload.storage_resource_id] : undefined) ||
          existingUrlsByFileName[fileName] ||
          existingAudioFiles[index]?.url ||
          null,
        isRemovable: canRemoveEntries,
      };
    });

    const existingOnlyEntries = existingAudioFiles
      .filter((file, index) => {
        const uploadAtIndex = uploads[index];
        const hasMatchingUpload = uploads.some((upload) => (file.file_id != null && upload.storage_resource_id === file.file_id) || (upload.file_name != null && upload.file_name === file.file_name));
        return !hasMatchingUpload && uploadAtIndex == null;
      })
      .map((file, index) => ({
        id: `existing:${file.file_id ?? index}:${file.file_name}`,
        fileName: file.file_name,
        durationSeconds: null,
        uploadedAt: 0,
        sourceUrl: file.url,
        isRemovable: canRemoveEntries,
      }));

    return [...uploadedEntries, ...existingOnlyEntries, ...pendingLocalEntries].sort((a, b) => b.uploadedAt - a.uploadedAt);
  }, [canRemoveEntries, existingAudioFiles, existingUrlsByFileId, existingUrlsByFileName, localAudioPreviews, pendingLocalEntries, uploads]);

  const setPlaybackRate = React.useCallback((audioId: string, rate: number) => {
    setPlaybackRates((previous) => {
      const audio = audioRefs.current[audioId];
      if (audio) {
        audio.playbackRate = rate;
      }
      return { ...previous, [audioId]: rate };
    });
  }, []);

  const toggleAudioPlayback = React.useCallback(
    async (audioId: string) => {
      const audio = audioRefs.current[audioId];
      if (!audio) {
        return;
      }

      if (playingAudioId === audioId) {
        audio.pause();
        audio.currentTime = 0;
        setPlayingAudioId(null);
        return;
      }

      if (playingAudioId && audioRefs.current[playingAudioId]) {
        const previousAudio = audioRefs.current[playingAudioId];
        previousAudio.pause();
        previousAudio.currentTime = 0;
      }

      try {
        audio.playbackRate = playbackRates[audioId] ?? 1;
        await audio.play();
        setPlayingAudioId(audioId);
      } catch (error) {
        console.error(error);
      }
    },
    [playbackRates, playingAudioId],
  );

  const removeAudioEntry = React.useCallback(
    (audioId: string) => {
      if (!canRemoveEntries) {
        return;
      }
      const currentUploads = audioNote?.uploads ?? [];
      const nextUploads = currentUploads.filter((upload, index) => {
        const fallbackId = `${upload.uuid ?? "uploaded"}:${upload.file_name ?? "audio"}:${index}`;
        return (upload.client_id ?? fallbackId) !== audioId;
      });

      const nextAudioNote: PatientAudioNoteValue = {
        ...(audioNote ?? {}),
        uploads: nextUploads,
        transcript_text: "",
        transcript_status: nextUploads.length > 0 ? "processing" : "empty",
      };
      dispatch(smartPrescriptionRecipeUpdateStarted());
      onChange(nextAudioNote);
      void persistAudioNote(nextAudioNote);
      setLocalAudioPreviews((prev) => {
        const nextPreviews = { ...prev };
        if (nextPreviews[audioId]?.startsWith("blob:")) {
          URL.revokeObjectURL(nextPreviews[audioId]);
        }
        removePendingAudioUpload(audioId);
        delete nextPreviews[audioId];
        return nextPreviews;
      });

      if (playingAudioId === audioId) {
        setPlayingAudioId(null);
      }
    },
    [audioNote, canRemoveEntries, dispatch, onChange, persistAudioNote, playingAudioId],
  );

  const formatUploadTimestamp = React.useCallback((uploadedAt: number) => {
    if (!uploadedAt) {
      return "";
    }

    const uploadedDate = new Date(uploadedAt);
    const now = new Date();
    const isToday = uploadedDate.getFullYear() === now.getFullYear() && uploadedDate.getMonth() === now.getMonth() && uploadedDate.getDate() === now.getDate();

    if (isToday) {
      return `Сегодня, ${uploadedDate.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    }

    return uploadedDate.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const setAudioRef = React.useCallback((id: string, node: HTMLAudioElement | null) => {
    audioRefs.current[id] = node;
  }, []);

  const openFilePicker = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    isLocked,
    isUploading,
    isRecording,
    hasDurationLimitReached,
    recordingDurationSeconds,
    levelHistory,
    totalDurationFormatted,
    maxDurationFormatted,
    liveTotalDurationFormatted,
    uploadProgressPercent,
    errorMessage,
    removeDisabledTooltip,
    allAudioEntries,
    playingAudioId,
    setPlayingAudioId,
    playbackRates,
    setPlaybackRate,
    fileInputRef,
    setAudioRef,
    formatDuration,
    formatUploadTimestamp,
    startRecording,
    stopRecording,
    handleFileUpload,
    toggleAudioPlayback,
    removeAudioEntry,
    openFilePicker,
  };
}

export type AudioNoteController = ReturnType<typeof useAudioNoteController>;
